import type { IssuedQuoteVersion, QuoteDestination } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { formatDateTime, formatWon } from '@/shared/lib';
import { ContentCard, QuoteDocument, quoteDestinationsForDocument } from '@/shared/ui';

export interface QuoteIssuedVersionsProps {
  readonly versions: readonly IssuedQuoteVersion[];
  readonly quoteNumber: string;
  readonly requestedAt: string;
  /** 견적 배송지(sortOrder 순) — 견적서 배송지 표에 받는 분·주소·수량·배송비로 적는다. 비면 표를 그리지 않는다. */
  readonly destinations: readonly QuoteDestination[];
}

/**
 * 발행본 버전별 열람(견적 설계 §6-2) — 최신 차수부터, 각 차수를 공용 견적서(QuoteDocument)로 그린다. 요약 줄의 합계 옆에 그 안의
 * 배송비(발행본 응답의 `shippingAmount`, 0원이어도)를 적는다.
 */
export const QuoteIssuedVersions = ({
  versions,
  quoteNumber,
  requestedAt,
  destinations,
}: QuoteIssuedVersionsProps): ReactElement => {
  const ordered = [...versions].sort((a, b) => b.version - a.version);
  const documentDestinations = quoteDestinationsForDocument(destinations);

  return (
    <ContentCard title="발행본">
      {ordered.length === 0 ? (
        <p className="text-sm text-muted-foreground">아직 발행본이 없습니다.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {ordered.map((version, index) => (
            <details
              key={version.id}
              open={index === 0}
              className="rounded-xl border border-foreground/[0.08]"
            >
              <summary className="cursor-pointer px-4 py-3 text-sm font-medium">
                {version.version}차 발행본 · {formatDateTime(version.issuedAt)} ·{' '}
                {formatWon(version.totalAmount)} (배송비 {formatWon(version.shippingAmount)} 포함)
              </summary>
              <div className="border-t border-foreground/[0.06] p-4">
                <QuoteDocument
                  version={version}
                  quoteNumber={quoteNumber}
                  requestedAt={requestedAt}
                  destinations={documentDestinations}
                />
              </div>
            </details>
          ))}
        </div>
      )}
    </ContentCard>
  );
};
